import UAParser from "ua-parser-js";
import semver from 'semver';
import {iosVersionSupportRealtimePitchShifting} from "../../config/realtime-pitch-shifting";
import {isDefined} from "../../util/is-type";
import {logger} from "../log/logger";
import {IDeviceHardwareStore} from "../../common/interfaces/IDeviceHardwareStore";
import {IDeviceBrowserStore} from "../../common/interfaces/IDeviceBrowserStore";
import {IWindowDeviceInfo} from "./IWindowDeviceInfo";

const userAgentParser = new UAParser();

const phonePortraitMaxWidth = `480px`;
const phoneLandscapeMaxWidth = `1040px`;
const phoneLandscapeMediaQuery = `only screen and (orientation: landscape) and (width <= ${phoneLandscapeMaxWidth})`;
const phonePortraitMediaQuery = `only screen and (orientation: portrait) and (width <= ${phonePortraitMaxWidth}) `;

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  // noinspection JSUnusedGlobalSymbols
  interface Window {
    deviceInfo?: IWindowDeviceInfo;
  }
}

const ios = "iOS";
const android = "android";

class DeviceInfo {
  #device: IWindowDeviceInfo;
  #hardware: IDeviceHardwareStore;
  #browser: IDeviceBrowserStore;

  constructor() {
    // load device info into local variable for possible faster lookup
    this.#device = this.#parseDeviceInfo();
    this.#hardware = this.#parseDeviceHardware();
    this.#browser = this.#parseDeviceBrowser();
  }

  /**
   * load device info first from window.deviceInfo if set with fall back to
   * uaParser
   */
  #parseDeviceInfo(): IWindowDeviceInfo {
    if (isDefined(window.deviceInfo)) {
      return window.deviceInfo;
    }

    const uaParserOs = userAgentParser.getOS();

    return {
      os: uaParserOs.name ?? `unknown`,
      deviceInfoVersion: `web`,
      osVersion: uaParserOs.version,
    };
  }

  #parseDeviceHardware(): IDeviceHardwareStore {
    const {model, type, vendor} = userAgentParser.getDevice();
    const cpu = userAgentParser.getCPU();

    return {
      model,
      type,
      vendor,
      cpu,
    };
  }

  #parseDeviceBrowser() {
    const {name, version, major} = userAgentParser.getBrowser();
    const userAgent = userAgentParser.getUA();

    return {
      name,
      version,
      major,
      userAgent,
    };
  }

  /**
   * Detects if the device is a phone across all platforms
   * Uses a multi-layered approach:
   * 1. OS detection (phones only run iOS or Android)
   * 2. UAParser device type detection
   * 3. Excludes tablets (tablets are mobile but not phones)
   * 4. Falls back to screen size as last resort
   */
  #isPhone(): boolean {
    const os = this.getOS();
    
    // Phones only run on mobile operating systems
    // Desktop OSes (Windows, Mac OS) are never phones
    if (os !== ios && os !== android) {
      return false;
    }

    // For iOS, check if it's NOT an iPad (iPad is tablet, not phone)
    if (os === ios) {
      // If native app provides isPad info, use it
      if (window.deviceInfo?.isPad !== undefined) {
        return !window.deviceInfo.isPad;
      }
    }

    // Use UAParser device type detection
    const deviceType = this.#hardware.type;
    
    // If UAParser detected it as mobile (not tablet), it's a phone
    if (deviceType === 'mobile') {
      return true;
    }
    
    // If UAParser detected it as tablet, it's not a phone
    if (deviceType === 'tablet') {
      return false;
    }

    // Fallback: Use screen size detection for cases where UAParser 
    // doesn't provide device type (some Android browsers)
    // This is less reliable but better than nothing
    const phonePortrait = window.matchMedia(phonePortraitMediaQuery);
    const phoneLandscape = window.matchMedia(phoneLandscapeMediaQuery);
    return phonePortrait.matches || phoneLandscape.matches;
  }

  get deviceInfo(): IWindowDeviceInfo {
    return this.#device;
  }

  get deviceHardwareInfo(): IDeviceHardwareStore {
    return this.#hardware;
  }

  get deviceBrowserInfo(): IDeviceBrowserStore {
    return this.#browser;
  }

  getOS(): Optional<string> {
    return window.deviceInfo?.os || userAgentParser.getOS().name;
  }

  getOsVersion(): Optional<string> {
    return window.deviceInfo?.osVersion ?? userAgentParser.getOS().version;
  }

  getIsIOS(): boolean {
    return this.getOS() === ios;
  }

  getIsAndroid(): boolean {
    return this.getOS() === android;
  }

  canDoRealtimePitchShifting(): boolean {
    const os = this.getOS();

    if (os === android) {
      return false;
    }

    // get details of os version
    const [osVersionMajor, osVersionMinor, osVersionPatch] = (this.getOsVersion() ?? `0`)
      // trim to remove any white space around
      .trim()
      // split and collect first 3 parts
      .split(`.`, 3);

    // build os version with all needed parts in correct format
    const osVersion = `${osVersionMajor}.${osVersionMinor ?? 0}.${osVersionPatch ?? 0}`

    // realtime pitch shifting if iOS
    if (os === ios) {
      /**
       * realtime pitch shifting for iOS
       * support all versions excluding 14.4.1-14.4.6
       * due to having issues with realtime pitch shifting
       */

      try {
        // get if iOS is able to do RealtimePitchShifting
        return semver.satisfies(osVersion, iosVersionSupportRealtimePitchShifting);
      } catch (e) {
        // attempt to log and resolve if there was an error
        logger.device.warn({
          message: 'canDoRealtimePitchShifting:ios-error',
          data: [
            {key: "osVersion", value: osVersion},
            {key: "iosVersionSupportRealtimePitchShifting", value: iosVersionSupportRealtimePitchShifting},
            {key: "error", value: e},
          ],
        });
        // return using old method as an attempt to recover
        return (osVersion.indexOf("14.4.") >= 0);
      }
    }

    // realtime pitch shifting if Mac
    if (os === `Mac OS` || os === `darwin`) {
      return true;
    }

    // realtime pitch shifting if Windows
    if (os === `Windows` || os === `win32`) {
      return false;
    }

    // no realtime pitch shifting if unknown
    return false;
  }

  /**
   * This is equivalent to the Redux hook useIsDeviceMobile or selector ReduxStateService.getSelector(getDeviceIsMobile)
   */
  getIsMobile() {
    const deviceInfo = window.deviceInfo;
    if (!deviceInfo || this.getIsAndroid()) {
      return this.#isPhone();
    }
    //web can be mismatched with native release, so check if the data necessary to detect mobile is provided in this version of the native app.
    if (deviceInfo.deviceInfoVersion !== "v2") {
      return false;
    }

    /**
     * `isPad` is iOS-only. It won't exist for Android, nor is there an equivalent.
     * @link https://reactnative.dev/docs/platform#ispad-ios
     */
    if (deviceInfo.isPad) {
      return false;
    }

    return this.getIsIOS();
  }

  /**
   * @deprecated use hook useIsDeviceTabletIPad or selector via ReduxStateService.getSelector(getDeviceIsPad)
   */
  getIsIPad() {
    return this.getIsIOS() && this.getIsTablet();
  }

  /**
   * Detects if the device is a tablet across all platforms (iOS, Android, Windows, MacOS)
   * - For iOS: checks if device is an iPad
   * - For Android: checks device type from user agent
   * - For Windows/MacOS: checks device type (e.g., Surface tablets)
   */
  getIsTablet(): boolean {
    // For iOS, check if it's an iPad
    if (this.getIsIOS()) {
      return window.deviceInfo?.isPad === true;
    }
    
    // For Android and other platforms, check device type from UAParser
    // UAParser detects tablets as type 'tablet'
    const deviceType = this.#hardware.type;
    return deviceType === 'tablet';
  }

  /**
   * Detects if the device is a desktop across all platforms (MacOS, Windows, iOS, Android)
   * - For iOS: always returns false (mobile/tablet OS)
   * - For Android: always returns false (mobile/tablet OS)
   * - For MacOS: returns true if not a mobile device
   * - For Windows: returns true if not a mobile/tablet device
   * 
   * @deprecated use hook useIsDeviceDesktop or selector via ReduxStateService.getSelector(getDeviceIsDesktop)
   */
  getIsDesktop(): boolean {
    // Check native app info first if available
    if (window.deviceInfo?.isDesktop !== undefined) {
      return window.deviceInfo.isDesktop;
    }

    const os = this.getOS();

    // iOS and Android are mobile/tablet platforms, not desktop
    if (os === ios || os === android) {
      return false;
    }

    // For Mac OS and Windows, check if it's NOT a mobile or tablet device
    if (os === `Mac OS` || os === `darwin` || os === `Windows` || os === `win32`) {
      // Check if it's a mobile device
      const isMobile = this.getIsMobile();
      const isTablet = this.getIsTablet();
      
      // Desktop if it's not mobile and not tablet
      const isDesktop = !isMobile && !isTablet;
      return isDesktop;
    }

    // For unknown OS, assume desktop if device type is not mobile/tablet
    const deviceType = this.#hardware.type;
    return deviceType !== 'mobile' && deviceType !== 'tablet';
  }

  getBrowserInfo() {
    return {
      name: this.getOS(),
      version: this.#browser.version,
      osVersion: this.#device.osVersion,
      canDoRealTimePitchShifting: this.canDoRealtimePitchShifting(),
    };
  }
}

const deviceInfo = new DeviceInfo();

export default deviceInfo;
